import type { TargetType } from "@/types";

/**
 * Constantes centralizadas do aplicativo RadiAI
 * Use estas constantes ao invés de magic numbers espalhados pelo código
 */

// ============= Limites de Caracteres =============
export const CHAR_LIMITS = {
  // Perfil
  NAME: 120,
  DISPLAY_NAME: 120,
  FULL_NAME: 120,
  BIO: 1000,
  
  // Target/Cliente
  TARGET_NAME: 120,
  TARGET_TAG: 50,
  TARGET_NOTES: 2000,
  
  // Sessão
  SESSION_OBJETIVO: 500,
  SESSION_OBSERVACOES: 2000,
  
  // Wizard
  WIZARD_PREPARATION_NOTES: 2000,
  WIZARD_CAUSA_RAIZ_DESC: 1000,
  WIZARD_RECOMENDACOES: 3000,
  WIZARD_RECOMENDACOES_ESPIRITUAIS: 3000,
  WIZARD_LIFE_AREA_OBSERVATION: 500,
  
  // Geral
  EMAIL: 255,
  PASSWORD_MIN: 8,
  PASSWORD_MAX: 128,
  AREAS_MAX_ITEMS: 20,
  AREAS_ITEM_LENGTH: 100,
} as const;

// ============= Tempos de Cache (React Query) =============
export const CACHE_TIMES = {
  /** Tempo até dados ficarem "stale" e precisarem refetch em background */
  STALE_TIME: 5 * 60 * 1000, // 5 minutos
  
  /** Tempo que dados ficam em cache após componente desmontar */
  GC_TIME: 30 * 60 * 1000, // 30 minutos (era cacheTime)
  
  /** Stale time para dados que mudam raramente (planos, blog posts) */
  STALE_TIME_LONG: 30 * 60 * 1000, // 30 minutos
  
  /** Stale time para dados do usuário (perfil, subscription) */
  STALE_TIME_USER: 30 * 1000, // 30 segundos
  
  /** Stale time curto para dashboards/dados que mudam frequentemente */
  STALE_TIME_SHORT: 30 * 1000, // 30 segundos
  
  /** Stale time para painéis admin */
  STALE_TIME_ADMIN: 60 * 1000, // 1 minuto
} as const;

// ============= SEO =============
export const SEO_LIMITS = {
  META_TITLE: 60,
  META_DESCRIPTION: 160,
  EXCERPT: 250,
} as const;

// ============= AI/Relatórios =============
export const AI_CONFIG = {
  /** Máximo de tokens para geração de relatórios */
  MAX_TOKENS_REPORT: 8192,
  
  /** Modelo padrão para relatórios */
  MODEL_REPORT: 'google/gemini-2.5-flash',
  
  /** Temperatura para geração de relatórios */
  TEMPERATURE_REPORT: 0.7,
} as const;

// ============= Rate Limiting (para referência frontend) =============
export const RATE_LIMITS = {
  /** Requests por minuto por endpoint */
  TARGETS: 30,
  SESSIONS: 30,
  PROFILE_UPDATE: 30,
  CHECKOUT: 10,
  PORTAL: 10,
  REPORT_GENERATION: 5,
  ADMIN_ROLES: 15,
  AI_USAGE: 20,
} as const;

// ============= Compartilhamento =============
export const SHARE_CONFIG = {
  /** Dias até link de compartilhamento expirar */
  EXPIRATION_DAYS: 30,
  
  /** URL base para links públicos */
  PUBLIC_BASE_URL: 'https://radiai.com.br',
} as const;

// ============= Planos =============
export const PLAN_LIMITS = {
  FREE: 3,
  PROFESSIONAL: 30,
  PREMIUM: null, // ilimitado
} as const;

export const PLAN_IDS = {
  FREE: 'free',
  PROFESSIONAL: 'profissional',
  PREMIUM: 'premium',
} as const;

// ============= Wizard =============
export const WIZARD_CONFIG = {
  /** Total de passos no wizard */
  TOTAL_STEPS: 6,
  
  /** Intervalo de auto-save (ms) */
  AUTO_SAVE_INTERVAL: 30000, // 30 segundos
  
  /** Debounce para auto-save (ms) */
  AUTO_SAVE_DEBOUNCE: 2000, // 2 segundos
} as const;

// ============= Escala Bóvis =============
export const BOVIS_CONFIG = {
  MIN: 0,
  MAX: 20000,
  DEFAULT: 6500,
  IDEAL: 6500,
  EXCELLENT_MAX: 7500,
  VERY_HIGH_MAX: 8000,
} as const;

// ============= UI/Animações =============
export const UI_CONFIG = {
  /** Duração padrão de transições (ms) */
  TRANSITION_DURATION: 300,
  
  /** Delay máximo para stagger de animações (ms) */
  FADE_IN_MAX_DELAY: 500,
  
  /** Debounce para busca (ms) */
  SEARCH_DEBOUNCE: 300,
  
  /** Tamanho de página para tabelas */
  TABLE_PAGE_SIZE: 10,
  
  // ============= Limites de Exibição =============
  
  /** Limite de rascunhos pendentes no dashboard */
  PENDING_DRAFTS_LIMIT: 5,
  
  /** Limite de sessões recentes no dashboard */
  RECENT_SESSIONS_LIMIT: 3,
  
  /** Máximo de itens em dropdowns de busca */
  DROPDOWN_MAX_ITEMS: 50,
  
  /** Limite de logs admin por página */
  ADMIN_LOGS_LIMIT: 100,
  
  /** Limite de logs de segurança na tabela */
  SECURITY_LOGS_TABLE_LIMIT: 50,
  
  /** Limite de posts relacionados */
  RELATED_POSTS_LIMIT: 2,
  
  /** Limite de preview de sessões em popover */
  SESSION_PREVIEW_LIMIT: 5,
  
  /** Limite de próximos agendamentos no calendário */
  UPCOMING_APPOINTMENTS_LIMIT: 10,
  
  // ============= Delays e Timeouts =============
  
  /** Delay para mostrar dialog de follow-up (ms) */
  FOLLOW_UP_DIALOG_DELAY: 2000,
  
  /** Delay para toast de sucesso auto-hide (ms) */
  TOAST_SUCCESS_DELAY: 2000,
  
  /** Delay de loading para evitar flash (ms) */
  LOADING_DELAY: 300,
  
  /** Timeout para operações async (ms) */
  ASYNC_OPERATION_DELAY: 300,
  
  /** Delay de redirecionamento após navegação (ms) */
  NAVIGATION_REDIRECT_DELAY: 500,
  
  /** Delay de redirecionamento lento (ms) */
  NAVIGATION_REDIRECT_SLOW_DELAY: 1000,
  
  /** Delay de redirecionamento longo (ms) */
  NAVIGATION_REDIRECT_LONG_DELAY: 2000,
  
  /** Delay para reset de atalho de teclado (ms) */
  KEYBOARD_SHORTCUT_RESET_DELAY: 1000,
} as const;

// ============= Payload Sizes =============
export const PAYLOAD_LIMITS = {
  /** Tamanho máximo de payload em bytes */
  MAX_PAYLOAD_SIZE: 1024 * 1024, // 1MB
  
  /** Tamanho máximo de imagem em bytes */
  MAX_IMAGE_SIZE: 5 * 1024 * 1024, // 5MB
} as const;

// ============= Service Worker =============
/**
 * Configurações do Service Worker para cache e atualizações.
 * NOTA: O arquivo public/sw.js não pode importar estas constantes
 * pois roda em contexto isolado. Estas servem para main.tsx e referência.
 * Ao alterar valores aqui, atualize também em public/sw.js manualmente.
 */
export const SW_CONFIG = {
  /** Versão atual do cache do Service Worker */
  VERSION: 'radiai-v1.2.0',
  
  /** Intervalo de verificação de atualizações (ms) */
  UPDATE_CHECK_INTERVAL: 10 * 60 * 1000, // 10 minutos
  
  /** Timeout para requisições de navegação (ms) */
  NAVIGATION_TIMEOUT: 3000, // 3 segundos
  
  /** Timeout para chunks JS dinâmicos (ms) */
  CHUNK_TIMEOUT: 3000, // 3 segundos
} as const;

// ============= Tipos de Alvo Visíveis na Criação de Cliente =============
/**
 * Controla quais tipos de alvo aparecem no select de criação de cliente.
 * Clientes já existentes com outros tipos continuam funcionando normalmente.
 * Para reativar um tipo, adicione-o a este array.
 */
export const VISIBLE_TARGET_TYPES: TargetType[] = ["Pessoa"];

// ============= Breakpoints Responsivos =============
/**
 * Breakpoints centralizados para uso consistente em toda a aplicação.
 * Alinhados com Tailwind CSS: sm=640, md=768, lg=1024, xl=1280, 2xl=1536
 */
export const BREAKPOINTS = {
  /** Mobile: < 768px (Tailwind md) */
  MOBILE: 768,
  /** Tablet: >= 768px && < 1024px */
  TABLET: 1024,
  /** Desktop: >= 1024px (Tailwind lg) */
  DESKTOP: 1024,
  /** Large Desktop: >= 1280px (Tailwind xl) */
  LG: 1280,
  /** Extra Large: >= 1536px (Tailwind 2xl) */
  XL: 1536,
} as const;
